<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Tutorial/Expressions_in_Freon';
    let nextLink= '/Tutorial/In_Need_of_Scoping';
    
    import Figure from '$lib/figures/Figure.svelte';
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="projections-for-expressions-1"
  bind:intersecting={visibleById["projections-for-expressions-1"]}
  onIntersectChange={(visible) => setVisible("projections-for-expressions-1", visible)}
>Projections for Expressions</SectionComponent>
<p>In this lesson you’ll learn how to define projections for expressions so you can take full advantage of Freon’s built-in expression support.</p>
<SectionComponent tag="h2" 
  id="cleaning-up-the-gradescore-concept-2"
  bind:intersecting={visibleById["cleaning-up-the-gradescore-concept-2"]}
  onIntersectChange={(visible) => setVisible("cleaning-up-the-gradescore-concept-2", visible)}
>Cleaning up the <i>GradeScore</i> Concept</SectionComponent>
<p>From the previous lesson we introduced the <code>GradeScore</code> concept. Let’s make its projection match the requirements:</p>
<pre class="language-txt"><code class="language-txt">GradeC: Answer to questionX is correct AND the Number of Correct Answers = 3

GradeD: Answer to questionY is correct OR Answer to questionZ is correct AND the Number of Correct Answers &gt; 2</code></pre>
<p>Define the projection like this:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson5-defs/edu-scoring.edit#L1-L6

editor default

GradeScore &#123;[
    $&#123;self.grade&#125; : $&#123;self.expr&#125;
]&#125;</code></pre>
<SectionComponent tag="h2" 
  id="reference-shortcut-3"
  bind:intersecting={visibleById["reference-shortcut-3"]}
  onIntersectChange={(visible) => setVisible("reference-shortcut-3", visible)}
>Reference Shortcut</SectionComponent>
<p>Next, consider the <code>Answer to questionY is correct</code> part. In the metamodel this is represented by the <code>QuestionReference</code> concept, so the projection becomes:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson5-defs/edu-scoring.edit#L11-L14

QuestionReference &#123;
    [ Answer to $&#123;self.question&#125; is correct ]
    trigger = "question"
    referenceShortcut = $&#123;self.question&#125;</code></pre>
<p>In Lesson 2 (<a href="/Tutorial/Making_an_Editor">Making an Editor</a>) you saw <strong>triggers</strong>, which make editing faster. Here we also use a <strong>reference shortcut</strong>.</p>
<p>When a question reference is added, two nodes are actually created in the AST: an instance of <code>QuestionReference</code>, and its child, a core <code>FreNodeReference</code>. Normally, the user would select both. With a reference shortcut, selecting a reference that fits the <code>question</code> property <strong>automatically</strong> creates the <code>QuestionReference</code> wrapper—no double selection required.</p>
<p>Add the following projections as well. Nothing new here—just formatting the remaining pieces. Note that for <code>NrOfCorrectAnswers</code> we use a constant string.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson5-defs/edu-scoring.edit#L16-L22


NrOfCorrectAnswers &#123;
    [Number Of Correct Answers]
&#125;

NumberLiteralExpression &#123;
    [$&#123;value&#125;]</code></pre>
<SectionComponent tag="h1" 
  id="binary-expressions-4"
  bind:intersecting={visibleById["binary-expressions-4"]}
  onIntersectChange={(visible) => setVisible("binary-expressions-4", visible)}
>Binary Expressions</SectionComponent>
<p>Projecting binary expressions is straightforward: tell Freon which <strong>symbol</strong> to display as the operator.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson5-defs/edu-scoring.edit#L24-L53


///////////////////////////////////
/// Boolean AND and OR
//////////////////////////////////

OrExpression &#123;
    symbol = "or"
&#125;
AndExpression &#123;
    symbol = "and"
&#125;

///////////////////////////////////
/// Comparisons: &lt;=, &gt;=, &gt;, &lt;, ===
//////////////////////////////////

LessOrEqualsExpression &#123;
    symbol = "&lt;="
&#125;
GreaterOrEqualsExpression &#123;
    symbol = "&gt;="
&#125;
LessThenExpression &#123;
    symbol = "&lt;"
&#125;
GreaterThenExpression &#123;
    symbol = "&gt;"
&#125;
EqualsExpression &#123;
    symbol = "=="</code></pre>
<p>After generation, you’ll see expressions rendered as proper infix expressions.</p>
<Figure
imageName={'tutorial/Tutorial-lesson5-screenshot1.png'}
caption={'Editor after projections for expressions'}
figureNumber={1}
/>
<p>Binary expressions are shown with brackets to reflect the priorities defined in <code>edu-scoring.ast</code>. If you prefer not to see them, open the <strong>View</strong> menu and deselect <strong>Brackets</strong>.</p>
<p>Experiment with the projections so far. You may notice that, in the score for page <code>Theory101</code>, you can select questions from other pages—exactly what we <em>don’t</em> want. Teachers should only grade based on the current page’s questions. That means we need <strong>scoping</strong>, which we’ll cover in the next lesson.</p>


<PrevNextSection {prevLink} {nextLink} />